export type Environment = 'development' | 'production';

/**
 * CRITICAL: DO NOT MODIFY OR REMOVE THE ROOT DOMAIN CONDITION
 * 
 * The condition `hostname === 'spinnergames.fun'` is essential for root domain authentication.
 * This ensures that the root domain (spinnergames.fun) uses the PRODUCTION Firebase project
 * for authentication, which is required for the root domain to work properly.
 * 
 * If this condition is removed or modified, authentication will break on spinnergames.fun
 * and users will not be able to log in on the main domain.
 * 
 * This is a critical production configuration that must be maintained.
 */
export const getEnvironment = (): Environment => {
  // Check if we're in a browser environment
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    // Check for subdomain-based environment detection
    if (hostname.startsWith('dev.')) {
      return 'development';
    }
    // CRITICAL: Root domain must use production environment for authentication
    if (hostname.startsWith('prod.') || hostname === 'spinnergames.fun') {
      return 'production';
    }
    
    // Check for Firebase hosting URLs
    if (hostname === 'spinnergames-dev.web.app' || hostname.includes('spinnergames-dev')) {
      return 'development';
    }
    if (hostname === 'spinnergames-prod.web.app' || hostname.includes('spinnergames-prod')) {
      return 'production';
    }
    
    // Check for localhost development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'development';
    }
  }
  
  // Fallback to NODE_ENV
  return (import.meta.env.MODE || process.env.NODE_ENV) === 'production' 
    ? 'production' 
    : 'development';
};

// Helper function to check if we should use Firebase emulators (DEPRECATED - not needed for localhost)
export const shouldUseEmulators = (): boolean => {
  // Emulators are not needed for localhost development
  // Firebase works perfectly fine in development mode without emulators
  return false;
};

export const getCDNBaseUrl = (): string => {
  // Check if we're in a browser environment
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    // Use Firebase hosting URLs for CDN when custom subdomains aren't available
    if (hostname === 'spinnergames-dev.web.app' || hostname.includes('spinnergames-dev')) {
      return 'https://spinnergames-dev.web.app/cdn';
    }
    if (hostname === 'spinnergames-prod.web.app' || hostname.includes('spinnergames-prod')) {
      return 'https://spinnergames-prod.web.app/cdn';
    }
    
    // Use custom subdomains when available
    if (hostname.startsWith('dev.')) {
      return 'https://dev.spinnergames.fun/cdn';
    }
    if (hostname.startsWith('prod.')) {
      return 'https://prod.spinnergames.fun/cdn';
    }
    if (hostname === 'spinnergames.fun') {
      return 'https://spinnergames.fun/cdn';
    }
  }
  
  // Fallback based on environment
  const env = getEnvironment();
  if (env === 'production') {
    return 'https://spinnergames-prod.web.app/cdn';
  } else {
    return 'https://spinnergames-dev.web.app/cdn';
  }
};

export const getFirebaseProjectId = (): string => {
  const env = getEnvironment();
  
  if (env === 'production') {
    return 'spinnergames-prod';
  } else {
    return 'spinnergames-dev';
  }
};

export const getFirebaseAuthDomain = (): string => {
  const env = getEnvironment();
  
  if (env === 'production') {
    return 'spinnergames-prod.firebaseapp.com';
  } else {
    return 'spinnergames-dev.firebaseapp.com';
  }
};

export const getFirebaseStorageBucket = (): string => {
  const env = getEnvironment();
  
  if (env === 'production') {
    return 'spinnergames-prod.appspot.com';
  } else {
    return 'spinnergames-dev.appspot.com';
  }
};

// Environment-specific configurations
export const environmentConfig = {
  development: {
    cdnBaseUrl: 'https://dev.spinnergames.fun/cdn',
    firebaseProjectId: 'spinnergames-dev',
    firebaseAuthDomain: 'spinnergames-dev.firebaseapp.com',
    firebaseStorageBucket: 'spinnergames-dev.appspot.com',
    cacheDuration: 3600, // 1 hour for dev
    enableDebug: true,
    enableAnalytics: false
  },
  production: {
    cdnBaseUrl: 'https://spinnergames.fun/cdn',
    firebaseProjectId: 'spinnergames-prod',
    firebaseAuthDomain: 'spinnergames-prod.firebaseapp.com',
    firebaseStorageBucket: 'spinnergames-prod.appspot.com',
    cacheDuration: 2592000, // 30 days for prod
    enableDebug: false,
    enableAnalytics: true
  }
};

// Get current environment config
export const getCurrentConfig = () => {
  const env = getEnvironment();
  return environmentConfig[env];
};

// Debug helper (suppressed for cleaner console)
export const logEnvironment = () => {
  const env = getEnvironment();
  const config = getCurrentConfig();
  
  // Environment logging suppressed for cleaner console output
  // console.log('🌍 Environment:', env);
  // console.log('🔗 CDN Base URL:', config.cdnBaseUrl);
  // console.log('🔥 Firebase Project:', config.firebaseProjectId);
  // console.log('📊 Analytics Enabled:', config.enableAnalytics);
  // console.log('🐛 Debug Enabled:', config.enableDebug);
};
